import {useState} from "react"
import {APIProvider, Map, Marker} from "@vis.gl/react-google-maps"
import {useColorMode} from "components/ui-old/color-mode"

import {API, Mask, toaster} from "config"
import {Badge, Box, Button, Grid, GridItem, HStack, Separator, Stack, Text, useDisclosure} from "@chakra-ui/react"
import {UI} from "components"
import useBankAccounts from "hooks/useBankAccounts"
import useDistributor from "hooks/useDistributor"

import Bank from "./bank"

export default function Profile() {

    const colorMode = useColorMode()
    const distributor = useDistributor()
    const bank_accounts = useBankAccounts()
    const bankDialog = useDisclosure()
    const [removingId, setRemovingId]       = useState(null)
    const [settingDefaultId, setSettingDefaultId] = useState(null)

    const maxBank = distributor.data?.config?.max_bank_accounts_per_nucleus ?? 3
    const maxPix  = distributor.data?.config?.max_pix_keys_per_nucleus ?? 5
    const bankCount = bank_accounts.data?.filter(a => a.agency)?.length ?? 0
    const pixCount  = bank_accounts.data?.filter(a => a.pix_key)?.length ?? 0
    const atLimit = bankCount >= maxBank && pixCount >= maxPix

    const handleRemove = async (id) => {
        if (!window.confirm('Tem certeza que deseja remover a conta bancária?')) return
        setRemovingId(id)

        try {
            await API.delete(`/bank_accounts/${id}`)
            bank_accounts.refetch()
        } catch (err) {
            toaster.create({
                title: 'Não foi possível remover a conta bancária',
                description: err?.response?.data?.message,
                type: 'warning'
            })
        }

        setRemovingId(null)
    }

    const handleSetDefault = async (id) => {
        setSettingDefaultId(id)

        try {
            await API.put(`/bank_accounts/${id}/default`)
            toaster.create({ title: 'Conta padrão definida', type: 'success' })
            bank_accounts.refetch()
        } catch (err) {
            toaster.create({
                title: 'Não foi possível definir a conta padrão',
                description: err?.response?.data?.message,
                type: 'warning'
            })
        }

        setSettingDefaultId(null)
    }

    return (
        <>
            <Bank {...bankDialog} />

            <UI.Header title="Meu núcleo"/>

            <Grid templateColumns={{base: '1fr', sm: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)'}} gap={4}>

                <GridItem>
                    <UI.Box>
                        <Stack gap={3}>

                            <Text fontSize="xs" color="GrayText">Informações do perfil:</Text>

                            <Box>
                                <Text fontSize="sm" color="GrayText">Nome público:</Text>
                                <Text>{distributor.data?.name}</Text>
                            </Box>

                            <Box>
                                <Text fontSize="sm" color="GrayText">Responsável:</Text>
                                <Text fontSize="sm" fontWeight="bold">{distributor.data?.customer?.name}</Text>
                            </Box>

                            <Separator/>

                            <Text fontSize="xs" color="GrayText">Dados da empresa:</Text>

                            <Box>
                                <Text fontSize="sm" color="GrayText">Razão social:</Text>
                                <Text fontSize="sm">{distributor.data?.company_name}</Text>
                            </Box>

                            <Box>
                                <Text fontSize="sm" color="GrayText">CNPJ:</Text>
                                <Text fontSize="sm">{Mask.cnpj(distributor.data?.doc_cnpj)}</Text>
                            </Box>

                            <Box>
                                <Text fontSize="sm" color="GrayText">Inscrição estadual:</Text>
                                <Text fontSize="sm">{distributor.data?.doc_ie}</Text>
                            </Box>

                        </Stack>

                    </UI.Box>
                </GridItem>

                <GridItem>
                    <UI.Box bg="transparent" borderWidth={1} borderStyle="dashed">
                        <Stack gap={3}>

                            <Text fontSize="xs" color="GrayText">Dados de contato:</Text>

                            <Box>
                                <Text fontSize="sm" color="GrayText">E-mail:</Text>
                                <Text fontSize="sm">{distributor.data?.email}</Text>
                            </Box>

                            <Box>
                                <Text fontSize="sm" color="GrayText">Telefone:</Text>
                                <Text fontSize="sm">{distributor.data?.mobile}</Text>
                            </Box>

                            <Separator/>

                            <Text fontSize="xs" color="GrayText">Localização:</Text>

                            <Box>
                                <Text fontSize="sm" color="GrayText">Cidade:</Text>
                                <Text
                                    fontSize="sm">
                                    {distributor.data?.city?.name},
                                    {distributor.data?.city?.state_code},
                                    {distributor.data?.city?.country_code}
                                </Text>
                            </Box>

                            <Box>
                                <Text fontSize="sm" color="GrayText">Endereço:</Text>
                                <Text fontSize="sm"
                                      textTransform="capitalize">
                                    {distributor.data?.address_line_1} {distributor.data?.address_line_2}
                                    | {distributor.data?.address_number}
                                    | {distributor.data?.address_complement}
                                </Text>
                            </Box>

                            <UI.Show visible={!!distributor?.data?.lat}>
                                <APIProvider apiKey="AIzaSyD0H9SsIRjE8mdlEKtPRRWElxzRzHtAqbg">
                                    <Map
                                        reuseMaps
                                        controlled
                                        style={{width: '100%', height: '120px', borderRadius: '10%'}}
                                        center={{lat: distributor?.data?.lat ?? 1, lng: distributor?.data?.lng ?? 1}}
                                        zoom={15}
                                        gestureHandling={'greedy'}
                                        disableDefaultUI={true}
                                        colorScheme={colorMode.colorMode === 'dark' ? 'DARK' : 'LIGHT'}

                                    >
                                        <Marker
                                            position={{lat: distributor?.data?.lat, lng: distributor?.data?.lng}}
                                            clickable={false}
                                        />
                                    </Map>
                                </APIProvider>
                            </UI.Show>

                        </Stack>
                    </UI.Box>
                </GridItem>

                <GridItem>
                    <UI.Box bg={bank_accounts.data?.length === 0 ? 'red.subtle' : null}>
                        <Stack gap={3}>
                            <Text fontSize="xs" color="GrayText">Dados bancários:</Text>

                            <UI.Show visible={bank_accounts.data?.length === 0}>
                                <UI.Alert size="sm" status="warning" title="Chave pix não cadastrada">Cadastre seus
                                    dados bancários para que os consultores possam gerar o QRCode Pix na hora do
                                    pagamento.</UI.Alert>
                            </UI.Show>

                            {bank_accounts.data?.map((account, index) => (
                                <Stack key={account.id} gap={2}>
                                    {index > 0 && <Separator />}

                                    <HStack justify="space-between" align="center">
                                        <Text fontSize="sm" fontWeight="medium">{account.bank?.name ?? <>&mdash;</>}</Text>
                                        {account.is_default && (
                                            <Badge colorPalette="green" size="sm">Padrão</Badge>
                                        )}
                                    </HStack>

                                    <Box>
                                        <Text fontSize="sm" color="GrayText">Tipo:</Text>
                                        <Text fontSize="sm">{account.type_label}</Text>
                                    </Box>

                                    <Box>
                                        <Text fontSize="sm" color="GrayText">Titular:</Text>
                                        <Text fontSize="sm">{account.name}</Text>
                                    </Box>

                                    <UI.Show visible={!!(account.agency && account.account)}>
                                        <Box>
                                            <Text fontSize="sm" color="GrayText">Agência/Conta:</Text>
                                            <Text fontSize="sm">{account.agency} / {account.account}</Text>
                                        </Box>
                                    </UI.Show>

                                    <UI.Show visible={!!account.pix_key}>
                                        <Box>
                                            <Text fontSize="sm" color="GrayText">Tipo de chave pix:</Text>
                                            <Text fontSize="sm">{account.pix_type_label}</Text>
                                        </Box>
                                        <Box>
                                            <Text fontSize="sm" color="GrayText">Chave pix:</Text>
                                            <Text fontSize="sm">{account.pix_key}</Text>
                                        </Box>
                                    </UI.Show>

                                    <HStack gap={2}>
                                        <UI.Show visible={!account.is_default}>
                                            <Button
                                                size="xs"
                                                colorPalette="green"
                                                variant="ghost"
                                                loading={settingDefaultId === account.id}
                                                loadingText="Definindo..."
                                                onClick={() => handleSetDefault(account.id)}
                                            >
                                                <UI.Icon name="check"/> Definir como padrão
                                            </Button>
                                        </UI.Show>

                                        <Button
                                            size="xs"
                                            colorPalette="red"
                                            variant="ghost"
                                            loading={removingId === account.id}
                                            loadingText="Removendo..."
                                            onClick={() => handleRemove(account.id)}
                                        >
                                            <UI.Icon name="trash"/> Remover
                                        </Button>
                                    </HStack>
                                </Stack>
                            ))}

                            <Separator />

                            <Button
                                size="sm"
                                onClick={bankDialog.onOpen}
                                disabled={atLimit}
                                title={atLimit ? `Limite atingido (${maxBank} contas + ${maxPix} chaves Pix)` : undefined}
                            >
                                <UI.Icon name="pix"/> Adicionar conta / chave pix
                            </Button>

                            <UI.Show visible={atLimit}>
                                <Text fontSize="xs" color="GrayText">
                                    Limite atingido: {maxBank} conta(s) bancária(s) e {maxPix} chave(s) Pix por núcleo.
                                </Text>
                            </UI.Show>
                        </Stack>
                    </UI.Box>
                </GridItem>

            </Grid>
        </>
    )
}